import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Form,
  Button,
  Input,
  DatePicker,
  Select,
  message,
  InputNumber,
  Row,
  Col,
  Space,
  Tag,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  TeamOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import {
  FileProtectOutlined,
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  CalendarOutlined,
  DollarOutlined,
  TagOutlined,
  PhoneOutlined,
  NumberOutlined,
} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/en_US";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
// استيراد مكونات الخصوم
import AddOpponentModal from "../opponents/AddOpponentModal";
import EditOpponentModal from "../opponents/EditOpponentModal";
import axiosInstance from "../../utils/axios.config";
import logger from "../../utils/logger";

// تفعيل plugin للتعامل مع التنسيقات المخصصة
dayjs.extend(customParseFormat);

const { TextArea } = Input;
const { Option } = Select;

const EditCaseModal = ({ visible, onCancel, onSubmit, initialData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [generatingNumber, setGeneratingNumber] = useState(false);
  const [clients, setClients] = useState([]);
  const [showClientsList, setShowClientsList] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const searchRef = useRef(null);
  const [opponents, setOpponents] = useState([]);
  const [showOpponentModal, setShowOpponentModal] = useState(false);
  const [showEditOpponentModal, setShowEditOpponentModal] = useState(false);
  const [editingOpponent, setEditingOpponent] = useState(null);
  const [opponentsList, setOpponentsList] = useState([]);
  const [showOpponentsList, setShowOpponentsList] = useState(false);
  const [opponentSearchTerm, setOpponentSearchTerm] = useState("");
  const opponentSearchRef = useRef(null);

  // Generate Case Number
  const generateCaseNumber = async () => {
    try {
      setGeneratingNumber(true);
      logger.debug("Starting to generate case number...");
      const currentYear = new Date().getFullYear();
      const cases = await axiosInstance.get("/cases");
      logger.debug("Fetched cases:", cases.data.data.length);

      // فلترة القضايا للسنة الحالية واستخراج الأرقام
      const yearCases = cases.data.data
        .filter((c) => c.case_number?.startsWith(currentYear.toString()))
        .map((c) => {
          const numberPart = c.case_number.split("/")[1];
          return parseInt(numberPart, 10);
        })
        .filter((num) => !isNaN(num)); // إزالة الأرقام غير الصحيحة

      // إيجاد أكبر رقم موجود
      const maxNumber = yearCases.length > 0 ? Math.max(...yearCases) : 0;
      const nextNumber = (maxNumber + 1).toString().padStart(3, "0");
      const newCaseNumber = `${currentYear}/${nextNumber}`;

      logger.debug(
        "Generated case number:",
        newCaseNumber,
        "from max:",
        maxNumber
      );
      logger.debug("Setting case number in form:", newCaseNumber);
      form.setFieldsValue({ case_number: newCaseNumber });

      // تأكد من أن القيمة تم تعيينها
      setTimeout(() => {
        const currentValue = form.getFieldValue("case_number");
        logger.debug("Case number after setting:", currentValue);
        if (!currentValue) {
          logger.debug("Case number not set, retrying...");
          form.setFieldsValue({ case_number: newCaseNumber });
        }
        setGeneratingNumber(false);
      }, 100);
    } catch (error) {
      logger.error("Error generating case number:", error);
      // في حالة الخطأ، استخدم timestamp لضمان التفرد
      const timestamp = Date.now().toString().slice(-3);
      const fallbackNumber = `${new Date().getFullYear()}/${timestamp}`;
      logger.debug("Using fallback number:", fallbackNumber);
      form.setFieldsValue({ case_number: fallbackNumber });
      setGeneratingNumber(false);
    }
  };

  // دالة لجلب بيانات العميل
  const fetchClientData = async (clientId) => {
    try {
      const response = await axiosInstance.get(`/clients/${clientId}`);
      if (response.data && response.data.data) {
        const clientData = response.data.data;
        setSelectedClient(clientData);
        form.setFieldsValue({
          client_name: clientData.name,
          client_id: clientData.id,
          phone: clientData.phone || "",
        });
        setSearchTerm(clientData.name);
      }
    } catch (error) {
      logger.error("Error fetching client data:", error);
      message.error("حدث خطأ في جلب بيانات العميل");
    }
  };

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Search clients function
  const searchClients = async (term) => {
    if (!term) {
      setClients([]);
      setShowClientsList(false);
      return;
    }

    try {
      const response = await axiosInstance.get(`/clients/search?term=${term}`);
      setClients(response.data.data || []);
      setShowClientsList(true);
    } catch (error) {
      logger.error("Error searching clients:", error);
      setClients([]);
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchTerm) => searchClients(searchTerm), 300),
    []
  );

  // Handle client search
  const handleClientSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (!selectedClient) {
      form.setFieldsValue({ client_name: term });
    }
    debouncedSearch(term);
  };

  // Handle client selection
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    form.setFieldsValue({
      client_name: client.name,
      client_id: client.id,
      phone: client.phone || "",
    });
    setShowClientsList(false);
    setSearchTerm(client.name);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowClientsList(false);
      }
      if (
        opponentSearchRef.current &&
        !opponentSearchRef.current.contains(event.target)
      ) {
        setShowOpponentsList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (visible && initialData) {
      try {
        const formatDate = (dateString) => {
          if (!dateString) return null;
          const date = dayjs(dateString);
          return date.isValid() ? date : null;
        };

        const formattedData = {
          ...initialData,
          filing_date: formatDate(
            initialData.filing_date_original || initialData.filing_date
          ),
          next_session_date: formatDate(
            initialData.next_session_date_original ||
              initialData.next_session_date
          ),
          phone: initialData.phone || "",
          agreed_fees: initialData.agreed_fees || null,
          // التأكد من تعيين الحقول الأساسية
          title: initialData.title || "",
          court_name: initialData.court_name || "",
          case_type: initialData.case_type || "",
          status: initialData.status || "جارية",
          description: initialData.description || "",
          notes: initialData.notes || "",
          court_case_number: initialData.court_case_number || "",
        };

        logger.debug("Initial Dates:", {
          original_filing: initialData.filing_date_original,
          original_next: initialData.next_session_date_original,
          formatted_filing: formattedData.filing_date,
          formatted_next: formattedData.next_session_date,
        });

        form.setFieldsValue(formattedData);

        // جلب الخصوم المرتبطين بالقضية
        fetchOpponents(initialData.id);

        // تهيئة متغيرات البحث عن الخصوم
        setOpponentSearchTerm("");
        setShowOpponentsList(false);
        setOpponentsList([]);

        // جلب بيانات العميل
        if (initialData.client_id) {
          fetchClientData(initialData.client_id);
        }
      } catch (error) {
        logger.error("Error formatting dates:", error);
        message.error("حدث خطأ في معالجة التواريخ");
      }
    } else {
      form.resetFields();
      setOpponents([]);
      setSelectedClient(null);
      setSearchTerm("");
      // إعادة تعيين متغيرات البحث عن الخصوم
      setOpponentSearchTerm("");
      setShowOpponentsList(false);
      setOpponentsList([]);
    }
  }, [visible, initialData, form]);

  // دالة لجلب الخصوم
  const fetchOpponents = async (caseId) => {
    try {
      const response = await axiosInstance.get(`/opponents/case/${caseId}`);
      logger.debug("Opponents response:", response);
      if (
        response.data &&
        (response.data.success || Array.isArray(response.data.data))
      ) {
        const opponentsData = response.data.data || [];
        setOpponents(opponentsData);

        // تحديث حقول النموذج المرتبطة بالخصوم
        if (opponentsData.length > 0) {
          const opponent = opponentsData[0]; // جلب الخصم الأول
          const opponentNames = opponentsData.map((op) => op.name).join(", ");

          // تحديث جميع حقول الخصم في النموذج
          form.setFieldsValue({
            opponent_name: opponent.name,
            opponent_id: opponent.id,
            opponent_phone: opponent.phone,
            opponent_type: opponent.opponent_type,
            opponent_address: opponent.address,
            opponent_lawyer: opponent.lawyer_name,
            opponent_lawyer_phone: opponent.lawyer_phone,
            opponent_lawyer_office: opponent.lawyer_office,
          });

          // تحديث حقل البحث
          setOpponentSearchTerm(opponent.name);
        } else {
          // مسح حقول الخصم إذا لم يتم العثور على خصوم
          form.setFieldsValue({
            opponent_name: "",
            opponent_id: null,
            opponent_phone: "",
            opponent_type: "",
            opponent_address: "",
            opponent_lawyer: "",
            opponent_lawyer_phone: "",
            opponent_lawyer_office: "",
          });
          setOpponentSearchTerm("");
        }
      }
    } catch (error) {
      logger.error("Error fetching opponents:", error);
      message.error("حدث خطأ في جلب الخصوم");
    }
  };

  // تعديل دالة handleSubmit
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const formatDate = (dateValue) => {
        if (!dateValue) return null;
        // تحويل التاريخ إلى تنسيق YYYY-MM-DD
        if (dayjs.isDayjs(dateValue)) {
          return dateValue.format("YYYY-MM-DD");
        }
        return dateValue;
      };

      // تحضير أسماء الخصوم
      const opponentNames =
        opponents.length > 0
          ? opponents.map((opponent) => opponent.name).join(", ")
          : "غير محدد";

      // تحضير معرفات الخصوم
      const opponentIds = opponents
        .map((opponent) => opponent.id)
        .filter((id) => id);
      const firstOpponentId = opponentIds.length > 0 ? opponentIds[0] : null;

      const formData = {
        ...values,
        case_number: values.case_number?.trim(),
        title: values.title?.trim(),
        client_name: values.client_name?.trim(),
        opponent_name: opponentNames,
        opponents: opponents, // إضافة قائمة الخصوم الكاملة
        opponent_ids: opponentIds, // إضافة مصفوفة معرفات الخصوم
        opponent_id: firstOpponentId, // إضافة معرف الخصم الأول كحقل منفرد
        court_name: values.court_name?.trim(),
        case_type: values.case_type?.trim(),
        status: values.status || "جديدة",
        filing_date: formatDate(values.filing_date),
        next_session_date: formatDate(values.next_session_date),
        description: values.description?.trim(),
        notes: values.notes?.trim(),
        client_id: values.client_id,
        phone: values.phone?.trim(),
        court_case_number: values.court_case_number?.trim(),
        agreed_fees: values.agreed_fees ? parseFloat(values.agreed_fees) : null,
      };

      logger.debug("Submitting form data:", formData); // للتأكد من البيانات
      await onSubmit(formData);
    } catch (error) {
      logger.error("Error updating case:", error);
      message.error("حدث خطأ في تحديث القضية");
    } finally {
      setLoading(false);
    }
  };

  // دالة لمعالجة إضافة خصم جديد
  const handleOpponentAdded = (newOpponent) => {
    setOpponents([...opponents, newOpponent]);
    setShowOpponentModal(false);
  };

  // دالة لمعالجة تعديل خصم
  const handleOpponentUpdated = (updatedOpponent) => {
    const updatedOpponents = opponents.map((opponent) =>
      opponent.id === updatedOpponent.id ? updatedOpponent : opponent
    );
    setOpponents(updatedOpponents);
    // تحديث قيمة الحقل المخفي لتفعيل التحقق
    form.setFieldsValue({
      opponents_validator: updatedOpponents.length > 0 ? "valid" : "",
    });
    // إعادة التحقق من صحة النموذج
    setTimeout(() => form.validateFields(["opponents_validator"]), 100);
    setShowEditOpponentModal(false);
    setEditingOpponent(null);
  };

  // دالة البحث عن الخصوم
  const searchOpponents = async (term) => {
    if (!term) {
      setOpponentsList([]);
      setShowOpponentsList(false);
      return;
    }

    try {
      const response = await axiosInstance.get(`/opponents?term=${term}`);
      setOpponentsList(response.data.data || []);
      setShowOpponentsList(true);
    } catch (error) {
      logger.error("Error searching opponents:", error);
      setOpponentsList([]);
    }
  };

  // Debounced search for opponents
  const debouncedOpponentSearch = useCallback(
    debounce((searchTerm) => searchOpponents(searchTerm), 300),
    []
  );

  // Handle opponent search
  const handleOpponentSearch = (e) => {
    const term = e.target.value;
    setOpponentSearchTerm(term);
    debouncedOpponentSearch(term);
  };

  // Handle opponent selection
  const handleOpponentSelect = (opponent) => {
    // تحديث حالة الخصم المحدد
    const selectedOpponent = {
      id: opponent.id,
      name: opponent.name?.trim(),
      phone: opponent.phone || "",
      status: opponent.status || "active",
    };

    // تحديث قائمة الخصوم
    setOpponents([selectedOpponent]);

    // تحديث النموذج بالبيانات الجديدة
    form.setFieldsValue({
      opponent_name: selectedOpponent.name,
      opponent_id: selectedOpponent.id,
      opponent_phone: selectedOpponent.phone,
    });

    // تحديث واجهة المستخدم
    setShowOpponentsList(false);
    setOpponentSearchTerm(selectedOpponent.name);
  };

  // دالة لحذف خصم
  const handleDeleteOpponent = async (opponentId) => {
    try {
      await axiosInstance.delete(`/opponents/${opponentId}`);
      const updatedOpponents = opponents.filter(
        (opponent) => opponent.id !== opponentId
      );
      setOpponents(updatedOpponents);
      message.success("تم حذف الخصم بنجاح");
    } catch (error) {
      logger.error("Error deleting opponent:", error);
      message.error("حدث خطأ في حذف الخصم");
    }
  };

  // تحديث مكونات DatePicker
  const datePickerProps = {
    locale: locale,
    format: "YYYY-MM-DD",
    style: { width: "100%" },
    suffixIcon: <CalendarOutlined className="text-primary" />,
    allowClear: true,
    getPopupContainer: (triggerNode) => triggerNode.parentNode,
    inputReadOnly: true,
    // تحديث معالجة التغيير
    onChange: (date, dateString) => {
      if (date) {
        return date.locale("en");
      }
      return null;
    },
  };

  return (
    <Modal
      title={
        <div className="text-xl font-bold text-right text-gray-800 mb-4">
          <FileProtectOutlined className="ml-2 text-primary" />
          تعديل القضية
        </div>
      }
      open={visible}
      onCancel={onCancel}
      okText="حفظ التعديلات"
      cancelText="إلغاء"
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={900}
      dir="rtl"
      className="custom-modal"
    >
      <Form
        dir="rtl"
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="px-4"
        initialValues={{
          status: "جارية",
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* رقم القضية */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="case_number"
                label="رقم القضية الداخلي"
                rules={[{ required: true, message: "رقم القضية مطلوب" }]}
              >
                <Input.Group compact>
                  <Input
                    prefix={<NumberOutlined />}
                    placeholder="رقم القضية"
                    style={{ width: "calc(100% - 40px)" }}
                    value={form.getFieldValue("case_number")}
                    onChange={(e) =>
                      form.setFieldsValue({ case_number: e.target.value })
                    }
                  />
                  <Button
                    type="default"
                    onClick={() => {
                      logger.debug("Manual regeneration clicked");
                      generateCaseNumber();
                    }}
                    style={{ width: "40px" }}
                    title="إعادة توليد رقم القضية"
                    loading={generatingNumber}
                  >
                    🔄
                  </Button>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="court_case_number" label="رقم القضية في المحكمة">
                <Input
                  prefix={<NumberOutlined />}
                  placeholder="رقم القضية في المحكمة"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* عنوان القضية */}
          <Form.Item
            name="title"
            label="عنوان القضية"
            rules={[{ required: true, message: "الرجاء إدخال عنوان القضية" }]}
          >
            <Input
              prefix={<FileTextOutlined className="ml-2" />}
              placeholder="أدخل عنوان القضية"
            />
          </Form.Item>

          {/* العميل مع البحث */}
          <div className="relative" ref={searchRef}>
            <Form.Item
              name="client_name"
              label="اسم العميل"
              rules={[{ required: true, message: "الرجاء اختيار العميل" }]}
            >
              <Input
                value={searchTerm}
                onChange={handleClientSearch}
                prefix={<UserOutlined className="ml-2" />}
                placeholder="ابحث عن عميل..."
              />
            </Form.Item>

            {/* حقل مخفي لـ client_id */}
            <Form.Item name="client_id" hidden>
              <Input />
            </Form.Item>

            {/* قائمة العملاء */}
            {showClientsList && clients.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    onClick={() => handleClientSelect(client)}
                  >
                    <div className="font-medium">{client.name}</div>
                    <div className="text-sm text-gray-500">
                      {client.phone && `رقم الهاتف: ${client.phone}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* حقل مخفي لـ client_id */}
          <Form.Item name="client_id" hidden>
            <Input />
          </Form.Item>

          {/* هاتف العميل */}
          <Form.Item name="phone" label="هاتف العميل">
            <Input
              prefix={<PhoneOutlined className="ml-2" />}
              placeholder="رقم هاتف العميل"
              disabled={!!selectedClient} // تعطيل الحقل إذا تم اختيار عميل
            />
          </Form.Item>

          {/* نوع القضية */}
          <Form.Item
            name="case_type"
            label="نوع القضية"
            rules={[{ required: true, message: "الرجاء اختيار نوع القضية" }]}
          >
            <Select placeholder="اختر نوع القضية">
              <Option value="مدني">مدني</Option>
              <Option value="جزائي">جزائي</Option>
              <Option value="تجاري">تجاري</Option>
              <Option value="عمالي">عمالي</Option>
              <Option value="أحوال شخصية">أحوال شخصية</Option>
            </Select>
          </Form.Item>

          {/* حقل الخصوم مع البحث */}
          <div className="relative" ref={opponentSearchRef}>
            <Form.Item
              label="الخصوم"
              required
              name="opponent_id"
              rules={[
                {
                  required: true,
                  message: "يجب اختيار الخصم",
                },
              ]}
            >
              <div className="flex items-center space-x-2 space-x-reverse">
                <Input
                  value={opponentSearchTerm}
                  onChange={handleOpponentSearch}
                  prefix={<UserOutlined className="ml-2" />}
                  placeholder="ابحث عن اسم الخصم..."
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowOpponentModal(true)}
                  title="إضافة خصم جديد"
                  className="px-3 py-1 text-sm"
                >
                  إضافة خصم
                </Button>
              </div>
            </Form.Item>

            {/* قائمة الخصوم */}
            {showOpponentsList && opponentsList.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                {opponentsList.map((opponent) => (
                  <div
                    key={opponent.id}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    onClick={() => handleOpponentSelect(opponent)}
                  >
                    <div className="font-medium">{opponent.name}</div>
                    {opponent.phone && (
                      <div className="text-sm text-gray-500">
                        رقم الهاتف: {opponent.phone}
                      </div>
                    )}
                    {opponent.address && (
                      <div className="text-sm text-gray-500">
                        العنوان: {opponent.address}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* حقول مخفية لتخزين بيانات الخصم */}
            <Form.Item name="opponent_name" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="opponent_phone" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="opponent_type" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="opponent_address" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="opponent_lawyer" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="opponent_lawyer_phone" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="opponent_lawyer_office" hidden>
              <Input />
            </Form.Item>
            {/* حقل التحقق من الخصوم */}
            <Form.Item name="opponents_validator" hidden>
              <Input />
            </Form.Item>

            {/* عرض الخصوم المضافة */}
            {opponents.length > 0 && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  الخصوم المضافة:
                </div>
                <div className="space-y-2">
                  {opponents.map((opponent, index) => (
                    <div
                      key={opponent.id || index}
                      className="flex items-center justify-between p-2 bg-white rounded border"
                    >
                      <div>
                        <span className="font-medium">{opponent.name}</span>
                        {opponent.phone && (
                          <span className="mr-3 text-sm text-gray-500">
                            ({opponent.phone})
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 space-x-reverse">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingOpponent(opponent);
                            setShowEditOpponentModal(true);
                          }}
                          title="تعديل بيانات الخصم"
                        />
                        <Button
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteOpponent(opponent.id)}
                          title="حذف الخصم"
                          danger
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* المحكمة */}
          <Form.Item
            name="court_name"
            label="المحكمة"
            rules={[{ required: true, message: "الرجاء إدخال اسم المحكمة" }]}
          >
            <Input prefix={<BankOutlined />} placeholder="اسم المحكمة" />
          </Form.Item>

          {/* تاريخ التسجيل */}
          <Form.Item
            name="filing_date"
            label="تاريخ التسجيل"
            rules={[{ required: true, message: "الرجاء اختيار تاريخ التسجيل" }]}
          >
            <DatePicker {...datePickerProps} placeholder="اختر التاريخ" />
          </Form.Item>

          {/* تاريخ الجلسة القادمة */}
          <Form.Item name="next_session_date" label="تاريخ الجلسة القادمة">
            <DatePicker
              {...datePickerProps}
              placeholder="اختر تاريخ الجلسة القادمة"
            />
          </Form.Item>

          {/* حالة القضية */}
          <Form.Item name="status" label="حالة القضية">
            <Select placeholder="اختر حالة القضية">
              <Option value="جديدة">جديدة</Option>
              <Option value="جارية">جارية</Option>
              <Option value="معلقة">معلقة</Option>
              <Option value="مكتملة">مكتملة</Option>
            </Select>
          </Form.Item>
        </div>

        {/* قيمة الأتعاب */}
        <Form.Item name="agreed_fees" label="قيمة الأتعاب">
          <InputNumber
            prefix={<DollarOutlined />}
            placeholder="أدخل قيمة الأتعاب"
            style={{ width: "100%" }}
            precision={0}
            step={1}
            min={0}
            max={999999999}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
          />
        </Form.Item>

        {/* تفاصيل القضية */}
        <Form.Item
          name="description"
          label="تفاصيل القضية"
          className="md:col-span-2"
        >
          <TextArea rows={4} placeholder="أدخل تفاصيل القضية" />
        </Form.Item>

        {/* ملاحظات */}
        <Form.Item name="notes" label="ملاحظات" className="md:col-span-2">
          <TextArea rows={3} placeholder="أدخل الملاحظات" />
        </Form.Item>

        {/* حقل خفي لمعرف العميل */}
        <Form.Item name="client_id" hidden>
          <Input />
        </Form.Item>
      </Form>

      {/* عرض معلومات العميل المختار */}
      {selectedClient && (
        <div className="mt-4 bg-blue-50 p-4 rounded-lg" dir="rtl">
          <h4 className="font-medium text-blue-800 mb-2">
            معلومات العميل المختار:
          </h4>
          <div
            className="grid grid-cols-2 gap-4 text-sm text-blue-600"
            dir="rtl"
          >
            <p>الاسم: {selectedClient.name}</p>
            <p>الهاتف: {selectedClient.phone || "غير متوفر"} </p>
            {selectedClient.email && <p>البريد: {selectedClient.email}</p>}
            {selectedClient.address && <p>العنوان: {selectedClient.address}</p>}
          </div>
        </div>
      )}

      <AddOpponentModal
        isOpen={showOpponentModal}
        onClose={() => setShowOpponentModal(false)}
        onOpponentAdded={(newOpponent) => {
          // استخراج معرف الخصم الحقيقي من الاستجابة
          const dbId = newOpponent.id || newOpponent._id;
          if (!dbId) {
            logger.error("لم يتم العثور على معرف الخصم من قاعدة البيانات!");
            return;
          }
          // إضافة الخصم الجديد إلى قائمة الخصوم مع معرف قاعدة البيانات فقط
          setOpponents((prevOpponents) => {
            // منع التكرار
            if (prevOpponents.some((op) => op.id === dbId))
              return prevOpponents;

            logger.debug("Adding opponent with DB ID:", dbId);

            // تأكد من أن جميع خصائص الخصم موجودة
            const opponent = {
              id: dbId,
              name: newOpponent.name,
              opponent_type: newOpponent.opponent_type || "individual",
              phone: newOpponent.phone || "",
              email: newOpponent.email || "",
              address: newOpponent.address || "",
              lawyer_name: newOpponent.lawyer_name || "",
              lawyer_phone: newOpponent.lawyer_phone || "",
              lawyer_office: newOpponent.lawyer_office || "",
              notes: newOpponent.notes || "",
              status: newOpponent.status || "active",
            };

            logger.debug("Formatted opponent object:", opponent);

            // تعيين معرف الخصم في النموذج مباشرة
            form.setFieldsValue({
              opponent_id: dbId,
            });

            const updatedOpponents = [...prevOpponents, opponent];
            return updatedOpponents;
          });
          // إغلاق النافذة فوراً بعد الإضافة
          setShowOpponentModal(false);
          // تعيين اسم الخصم في حقل البحث
          setOpponentSearchTerm(newOpponent.name);
          // مسح قائمة البحث
          setShowOpponentsList(false);
          // تحديث قيمة التحقق في النموذج
          form.setFieldsValue({ opponents_validator: "valid" });
          // تعيين قيمة حقل الخصم في النموذج
          form.setFieldsValue({ opponent_name: newOpponent.name });
        }}
        zIndex={1100}
        showSuccessMessage={false}
      />
      {/* نموذج تعديل خصم */}
      <Modal
        title="تعديل بيانات الخصم"
        open={showEditOpponentModal}
        onCancel={() => {
          setShowEditOpponentModal(false);
          setEditingOpponent(null);
        }}
        footer={null}
        width={800}
        zIndex={1050}
        style={{ top: 20 }}
      >
        <EditOpponentModal
          isOpen={showEditOpponentModal}
          onClose={() => {
            setShowEditOpponentModal(false);
            setEditingOpponent(null);
          }}
          opponentId={editingOpponent?.id}
          onOpponentUpdated={handleOpponentUpdated}
        />
      </Modal>
    </Modal>
  );
};

export default EditCaseModal;
